import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';

import { CumplimientoRepository } from 'src/app/domain/service/cumplimiento/cumplimiento-repository';
import { CatalogoRepository } from 'src/app/domain/service/catalogo/catalogo-repository';
import { CatalogoOption } from 'src/app/domain/model/catalogo/catalogo';
import {
  RiesgoPotencialFiltro,
  RiesgoPotencialListadoItem
} from 'src/app/domain/model/cumplimiento/cumplimiento';
import { abrirAlertaTemprana } from 'src/app/common/alerta-temprana.utils';
import { sanitizeText } from 'src/app/common/input-utils';
import { UsuarioStateService } from 'src/app/infrastructure/state/usuario-state.service';
import { DEFAULT_ROWS_PER_PAGE, PAGE_SIZE_OPTIONS } from 'src/app/common/constants-ejb';
import {
  BusquedaAsesorDialogComponent,
  AsesorSeleccionado
} from '../../common/busquedas/busqueda-asesor-dialog.component';
import {
  BusquedaClienteDialogComponent,
  ClienteSeleccionado
} from '../../common/busquedas/busqueda-cliente-dialog.component';
import { BusquedaProfesionalDialogComponent } from '../../common/busquedas/busqueda-profesional-dialog.component';
import { Profesional } from 'src/app/domain/model/profesional/profesional';
import { SeguimientoRiesgoPotencialComponent } from './seguimiento-riesgo-potencial.component';
import { BusquedaPaginadorComponent } from '../../shared/busquedas/busqueda-paginador.component';

/** Legacy: WidgetsNamesConstants.messagesFiltros */
const MSG_FILTROS_REQUERIDOS = 'Debe diligenciar al menos uno de los filtros';
/** Legacy: WidgetsNamesConstants.messagesFechas */
const MSG_FECHAS = 'La fecha final esperada debe ser mayor que la fecha inicial';
/** Legacy: WidgetsNamesConstants.busquedaNoArrojoResultado */
const MSG_SIN_RESULTADOS = 'No se recuperó información con los parámetros ingresados.';

/**
 * Container: Consulta de Alertas Tempranas (Riesgo Potencial)
 *
 * Legacy: RiesgoPotencialViewImpl.java + RiesgoPotencialViewImpl.ui.xml
 * Backend: GET /api/servicios/orientacionesProgramadas/riesgos-potenciales
 *
 * Filtros equivalentes al legado: orientación de servicio, oficina, rango de fecha de
 * identificación, rango de fecha de seguimiento, estado, riesgo identificado, profesional,
 * consultor y cliente.
 *
 * Legacy: WidgetsNamesConstants.properties -> idLabelAdvisor=Consultor, idLabelBeneficiary=Cliente.
 * Los campos internos (nmconsecutivoAsesor, nmconsecutivoBenef) conservan el nombre técnico
 * del modelo de datos, pero la etiqueta visible siempre fue "Consultor"/"Cliente" en el legado.
 *
 * Profesional, Consultor y Cliente reutilizan los diálogos de búsqueda compartidos
 * (BusquedaProfesionalDialogComponent, BusquedaAsesorDialogComponent, BusquedaClienteDialogComponent)
 * usados en el resto de módulos migrados, en vez de campos de texto libre.
 *
 * Legacy: RiesgoPotencialViewImpl.reset() precarga la oficina del usuario logueado
 * (ApplicationCommonData.getSessionUser().getNmconsec_oficina()) al entrar a la pantalla.
 *
 * Paginación keyset con cursor opaco emitido por el backend, ordenada por fecha de ingreso
 * ascendente (legacy: order by e.feingresa) para destacar las alertas más rezagadas.
 */
@Component({
  selector: 'app-riesgos-potenciales',
  standalone: true,
  styleUrls: ['../../shared/styles/grid-table.styles.scss'],
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    DatePickerModule,
    DialogModule,
    ToastModule,
    TooltipModule,
    BusquedaAsesorDialogComponent,
    BusquedaClienteDialogComponent,
    BusquedaProfesionalDialogComponent,
    SeguimientoRiesgoPotencialComponent,
    BusquedaPaginadorComponent
  ],
  providers: [MessageService],
  template: `
    <div class="p-3 sm:p-4 lg:p-6 max-w-7xl mx-auto overflow-x-hidden">
      <p-toast></p-toast>

      <div class="text-sm text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-2">
        <span>Cumplimientos</span>
        <span class="text-gray-400 dark:text-gray-500">»</span>
        <span class="font-medium text-gray-800 dark:text-gray-100">Alertas Tempranas</span>
      </div>

      <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 class="text-xl font-semibold text-sura-blue-800 dark:text-sura-blue-100">
          Consultar Alertas Tempranas
        </h1>
        <!-- Legacy: saNuevoRp — abre la aplicación externa de Alerta Temprana sin parámetros -->
        <button
          pButton
          type="button"
          label="Nueva Alerta Temprana SIN OC"
          icon="pi pi-external-link"
          class="p-button-outlined"
          (click)="nuevaAlertaTemprana()"
        ></button>
      </div>

      <section
        class="rounded-xl border border-sura-gray-200 bg-white p-4 shadow-sm dark:border-sura-gray-800 dark:bg-gray-900 mb-4"
      >
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div class="flex flex-col gap-1">
            <label for="fOrden" class="text-sm text-gray-600 dark:text-gray-400">Orientación de Servicio</label>
            <input
              pInputText
              id="fOrden"
              [(ngModel)]="filtro.dsorden"
              (input)="filtro.dsorden = sanitizeText(filtro.dsorden)"
              maxlength="30"
            />
          </div>

          <div class="flex flex-col gap-1">
            <label for="fOficina" class="text-sm text-gray-600 dark:text-gray-400">Oficina</label>
            <p-dropdown
              inputId="fOficina"
              [options]="oficinas()"
              [(ngModel)]="filtro.nmconsecutivoOficina"
              optionLabel="descripcion"
              optionValue="codigoNumerico"
              placeholder="Todas"
              [filter]="true"
              filterBy="descripcion"
              [showClear]="true"
              appendTo="body"
              styleClass="w-full"
            />
          </div>

          <div class="flex flex-col gap-1">
            <label for="fEstado" class="text-sm text-gray-600 dark:text-gray-400">Estado</label>
            <p-dropdown
              inputId="fEstado"
              [options]="estados()"
              [(ngModel)]="filtro.cdestado"
              optionLabel="descripcion"
              optionValue="codigo"
              [filter]="true"
              filterBy="descripcion"
              placeholder="Todos"
              [showClear]="true"
              appendTo="body"
              styleClass="w-full"
            />
          </div>

          <div class="flex flex-col gap-1">
            <label for="fRiesgo" class="text-sm text-gray-600 dark:text-gray-400">Riesgo identificado</label>
            <p-dropdown
              inputId="fRiesgo"
              [options]="agentesRiesgo()"
              [(ngModel)]="filtro.nmconsecRiesgo"
              optionLabel="descripcion"
              optionValue="codigoNumerico"
              placeholder="Todos"
              [filter]="true"
              filterBy="descripcion"
              [showClear]="true"
              appendTo="body"
              styleClass="w-full"
            />
          </div>

          <div class="flex flex-col gap-1">
            <label for="fIdentIni" class="text-sm text-gray-600 dark:text-gray-400">
              Fecha Inicial Identificación
            </label>
            <p-datepicker
              inputId="fIdentIni"
              [(ngModel)]="fechaIdentificacionInicial"
              dateFormat="yy/mm/dd"
              [showIcon]="true"
              appendTo="body"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label for="fIdentFin" class="text-sm text-gray-600 dark:text-gray-400">
              Fecha Final Identificación
            </label>
            <p-datepicker
              inputId="fIdentFin"
              [(ngModel)]="fechaIdentificacionFinal"
              dateFormat="yy/mm/dd"
              [showIcon]="true"
              appendTo="body"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label for="fSegIni" class="text-sm text-gray-600 dark:text-gray-400">
              Fecha Inicial Seguimiento
            </label>
            <p-datepicker
              inputId="fSegIni"
              [(ngModel)]="fechaSeguimientoInicial"
              dateFormat="yy/mm/dd"
              [showIcon]="true"
              appendTo="body"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label for="fSegFin" class="text-sm text-gray-600 dark:text-gray-400">
              Fecha Final Seguimiento
            </label>
            <p-datepicker
              inputId="fSegFin"
              [(ngModel)]="fechaSeguimientoFinal"
              dateFormat="yy/mm/dd"
              [showIcon]="true"
              appendTo="body"
            />
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
          <div class="flex flex-col gap-1">
            <label for="fProfesional" class="text-sm text-gray-600 dark:text-gray-400">Profesional</label>
            <div class="flex gap-1 min-w-0">
              <input pInputText id="fProfesional" [value]="nombreProfesional" readonly class="flex-1 min-w-0" />
              <button
                pButton
                type="button"
                icon="pi pi-search"
                class="p-button-outlined p-button-sm flex-shrink-0 min-w-[44px] min-h-[44px]"
                pTooltip="Buscar profesional"
                tooltipPosition="left"
                (click)="abrirBuscadorProfesional()"
              ></button>
              <button
                pButton
                type="button"
                icon="pi pi-times"
                class="p-button-text p-button-danger flex-shrink-0 min-w-[44px] min-h-[44px]"
                pTooltip="Limpiar profesional"
                tooltipPosition="left"
                [disabled]="!filtro.dniProfesional"
                (click)="limpiarProfesional()"
              ></button>
            </div>
          </div>

          <!-- Legacy: idLabelAdvisor=Consultor -->
          <div class="flex flex-col gap-1">
            <label for="fConsultor" class="text-sm text-gray-600 dark:text-gray-400">Consultor</label>
            <div class="flex gap-1 min-w-0">
              <input pInputText id="fConsultor" [value]="nombreConsultor" readonly class="flex-1 min-w-0" />
              <button
                pButton
                type="button"
                icon="pi pi-search"
                class="p-button-outlined p-button-sm flex-shrink-0 min-w-[44px] min-h-[44px]"
                pTooltip="Buscar consultor"
                tooltipPosition="left"
                (click)="abrirBuscadorConsultor()"
              ></button>
              <button
                pButton
                type="button"
                icon="pi pi-times"
                class="p-button-text p-button-danger flex-shrink-0 min-w-[44px] min-h-[44px]"
                pTooltip="Limpiar consultor"
                tooltipPosition="left"
                [disabled]="!filtro.nmconsecutivoAsesor"
                (click)="limpiarConsultor()"
              ></button>
            </div>
          </div>

          <!-- Legacy: idLabelBeneficiary=Cliente -->
          <div class="flex flex-col gap-1">
            <label for="fCliente" class="text-sm text-gray-600 dark:text-gray-400">Cliente</label>
            <div class="flex gap-1 min-w-0">
              <input pInputText id="fCliente" [value]="nombreCliente" readonly class="flex-1 min-w-0" />
              <button
                pButton
                type="button"
                icon="pi pi-search"
                class="p-button-outlined p-button-sm flex-shrink-0 min-w-[44px] min-h-[44px]"
                pTooltip="Buscar cliente"
                tooltipPosition="left"
                (click)="abrirBuscadorCliente()"
              ></button>
              <button
                pButton
                type="button"
                icon="pi pi-times"
                class="p-button-text p-button-danger flex-shrink-0 min-w-[44px] min-h-[44px]"
                pTooltip="Limpiar cliente"
                tooltipPosition="left"
                [disabled]="!filtro.nmconsecutivoBenef"
                (click)="limpiarCliente()"
              ></button>
            </div>
          </div>
        </div>

        <div class="mt-3 flex flex-wrap gap-2 sm:gap-3 justify-end sm:justify-start w-full">
          <!-- Legacy: siLimpiar -->
          <button
            pButton
            type="button"
            label="Limpiar"
            icon="pi pi-eraser"
            class="p-button-secondary p-button-outlined"
            (click)="limpiar()"
          ></button>
          <!-- Legacy: siBuscar -->
          <button pButton type="button" label="Buscar" icon="pi pi-search" (click)="buscar()"></button>
        </div>
      </section>

      @if (mensajeError()) {
        <div
          class="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-700 dark:bg-red-900/30 dark:text-red-300"
        >
          {{ mensajeError() }}
        </div>
      }

      @if (tablaVisible()) {
        <!-- Header de paginación (skill paginacion: Registros del X hasta el Y de Z) -->
        <div class="flex justify-between items-center mb-3">
          <span class="text-sm text-gray-600 dark:text-gray-400">
            @if ((totalRegistros() ?? 0) > 0) {
              Registros del {{ registroInicio() }} hasta el {{ registroFin() }} de {{ totalRegistros() }}
            }
          </span>
        </div>

        <!-- Mobile: cards (legacy: stRiesgoPotencial, 9 columnas -> tabla densa, ver skill sura-ui 11.6.1) -->
        <div class="md:hidden space-y-3">
          @for (item of items(); track item.nmconsecutivo) {
            <article
              class="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800"
            >
              <div class="flex items-start justify-between gap-2 mb-2">
                <h3
                  class="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate"
                  [title]="tituloTabla(item.dsnombreEmpresa)"
                >
                  {{ limitar(item.dsnombreEmpresa) }}
                </h3>
                <span class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  {{ item.feidentificacion | date: 'dd/MM/yyyy' }}
                </span>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mb-3">
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Orden:</span>
                  {{ textoTabla(item.dsorden) }}
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Oficina:</span>
                  {{ textoTabla(item.dsoficina) }}
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Contrato:</span>
                  {{ textoTabla(item.cdcontrato) }}
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Estado:</span>
                  {{ textoTabla(item.dsestado ?? item.cdestado) }}
                </div>
                <div class="sm:col-span-2" [title]="tituloTabla(item.dsriesgo)">
                  <span class="text-gray-500 dark:text-gray-400">Riesgo:</span>
                  {{ limitar(item.dsriesgo) }}
                </div>
                <div class="sm:col-span-2">
                  <span class="text-gray-500 dark:text-gray-400">Tipo de control:</span>
                  {{ textoTabla(item.dscontrol) }}
                </div>
              </div>

              <button
                pButton
                type="button"
                icon="pi pi-eye"
                label="Ver"
                class="p-button-outlined p-button-sm w-full min-h-[44px]"
                (click)="verDetalleSeguimiento(item)"
              ></button>
            </article>
          } @empty {
            <div class="text-center text-gray-500 dark:text-gray-400 py-4">{{ MSG_SIN_RESULTADOS }}</div>
          }
        </div>

        <!-- Desktop/tablet: tabla completa (densa: 9 columnas -> patrón grid-header-text/grid-cell-text, ver skill sura-ui 11.4.1) -->
        <div class="hidden md:block">
          <p-table
            [value]="items()"
            [loading]="loading()"
            [paginator]="false"
            [rows]="pageSize()"
            [scrollable]="true"
            responsiveLayout="scroll"
            scrollHeight="480px"
            [resizableColumns]="true"
            columnResizeMode="expand"
            [tableStyle]="{ 'min-width': '72rem' }"
            styleClass="tabla-riesgos-potenciales p-datatable-sm"
          >
            <ng-template pTemplate="header">
              <tr>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Orientación de servicio">Orientación de servicio</span>
                </th>
                <th pResizableColumn><span class="grid-header-text" title="Oficina">Oficina</span></th>
                <th pResizableColumn><span class="grid-header-text" title="Contrato">Contrato</span></th>
                <th pResizableColumn><span class="grid-header-text" title="Empresa">Empresa</span></th>
                <th pResizableColumn class="text-center">
                  <span class="grid-header-text" title="Fecha identificación">Fecha identificación</span>
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Riesgo identificado">Riesgo identificado</span>
                </th>
                <th pResizableColumn><span class="grid-header-text" title="Tipo de control">Tipo de control</span></th>
                <th pResizableColumn><span class="grid-header-text" title="Estado">Estado</span></th>
                <th pResizableColumn class="text-center">
                  <span class="grid-header-text" title="Acciones">Acciones</span>
                </th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-item>
              <tr>
                <td>
                  <span class="grid-cell-text" [attr.title]="tituloTabla(item.dsorden)">{{ textoTabla(item.dsorden) }}</span>
                </td>
                <td>
                  <span class="grid-cell-text" [attr.title]="tituloTabla(item.dsoficina)">{{ textoTabla(item.dsoficina) }}</span>
                </td>
                <td>
                  <span class="grid-cell-text" [attr.title]="tituloTabla(item.cdcontrato)">{{ textoTabla(item.cdcontrato) }}</span>
                </td>
                <td>
                  <span class="grid-cell-text" [attr.title]="tituloTabla(item.dsnombreEmpresa)">
                    {{ textoTabla(item.dsnombreEmpresa) }}
                  </span>
                </td>
                <td class="text-center">{{ item.feidentificacion | date: 'dd/MM/yyyy' }}</td>
                <td>
                  <span class="grid-cell-text" [attr.title]="tituloTabla(item.dsriesgo)">{{ textoTabla(item.dsriesgo) }}</span>
                </td>
                <td>
                  <span class="grid-cell-text" [attr.title]="tituloTabla(item.dscontrol)">{{ textoTabla(item.dscontrol) }}</span>
                </td>
                <td>
                  <span class="grid-cell-text" [attr.title]="tituloTabla(item.dsestado ?? item.cdestado)">
                    {{ textoTabla(item.dsestado ?? item.cdestado) }}
                  </span>
                </td>
                <td class="text-center">
                  <button
                    pButton
                    type="button"
                    icon="pi pi-eye"
                    class="p-button-rounded p-button-text p-button-sm min-w-[44px] min-h-[44px]"
                    pTooltip="Ver detalle"
                    tooltipPosition="left"
                    (click)="verDetalleSeguimiento(item)"
                  ></button>
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="9" class="text-center text-gray-500 dark:text-gray-400 py-4">
                  {{ MSG_SIN_RESULTADOS }}
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>

        <!-- Footer de paginación (skill paginacion: controles manuales, sin paginador automático de PrimeNG) -->
        @if ((totalRegistros() ?? 0) > 0) {
          <app-busqueda-paginador
            [currentPage]="currentPage()"
            [totalPages]="totalPages()"
            [hasPrevious]="hasPrevious()"
            [hasNext]="hasNext()"
            [pageSizeOptions]="pageSizeOptions"
            [selectedPageSize]="selectedPageSize"
            (pageSizeChange)="selectedPageSize = $event; onPageSizeChange()"
            (first)="irPrimeraPagina()"
            (previous)="irPaginaAnterior()"
            (next)="irPaginaSiguiente()"
            (last)="irUltimaPagina()"
          ></app-busqueda-paginador>
        }
      }

      <!-- Diálogos de búsqueda reutilizados (legacy: PersonSearcherInterfaceImpl / BuscarBeneficiarioViewImpl / BusquedaActivity) -->
      @if (mostrarBuscadorConsultor) {
        <app-asesor-search
          [titulo]="'Búsqueda de Consultor'"
          (onSelect)="onConsultorSeleccionado($event)"
          (onClose)="mostrarBuscadorConsultor = false"
        ></app-asesor-search>
      }

      @if (mostrarBuscadorCliente) {
        <app-busqueda-cliente-dialog
          (onSelect)="onClienteSeleccionado($event)"
          (onClose)="mostrarBuscadorCliente = false"
        ></app-busqueda-cliente-dialog>
      }

      @if (mostrarBuscadorProfesional) {
        <app-busqueda-profesional-dialog
          (onSelect)="onProfesionalSeleccionado($event)"
          (onClose)="mostrarBuscadorProfesional = false"
        ></app-busqueda-profesional-dialog>
      }

      <!-- Ver detalle -> modal en vez de navegación con el id expuesto en la URL (mismo patrón que servicios-entregados: maximizado por defecto) -->
      <p-dialog
        #seguimientoDialog
        header="Seguimiento de Alerta Temprana"
        [(visible)]="mostrarModalSeguimiento"
        (onHide)="cerrarModalSeguimiento()"
        (onShow)="maximizarDialogoSeguimiento(seguimientoDialog)"
        [modal]="true"
        [draggable]="false"
        [resizable]="false"
        [maximizable]="true"
        [closable]="false"
        [closeOnEscape]="false"
        [dismissableMask]="false"
        [style]="{ width: '92vw', maxWidth: '1400px' }"
        [contentStyle]="{ overflow: 'auto', maxHeight: '80vh' }"
      >
        @if (idRiesgoSeleccionado(); as idRiesgo) {
          <app-seguimiento-riesgo-potencial
            [idRiesgoInput]="idRiesgo"
            [modoModal]="true"
            (cerrar)="cerrarModalSeguimiento()"
          ></app-seguimiento-riesgo-potencial>
        }
      </p-dialog>
    </div>
  `
})
export class RiesgosPotencialesComponent implements OnInit {
  private readonly repository = inject(CumplimientoRepository);
  private readonly catalogoRepository = inject(CatalogoRepository);
  private readonly messageService = inject(MessageService);
  private readonly usuarioState = inject(UsuarioStateService);

  readonly MSG_SIN_RESULTADOS = MSG_SIN_RESULTADOS;

  // XSS Sanitization - exponer función al template
  sanitizeText = sanitizeText;

  readonly items = signal<RiesgoPotencialListadoItem[]>([]);
  readonly loading = signal(false);
  readonly hasNext = signal(false);
  readonly hasPrevious = signal(false);
  readonly totalRegistros = signal<number | null>(null);
  readonly mensajeError = signal<string | null>(null);
  /** Legacy: stRiesgoPotencial.setVisible(false) hasta que la búsqueda arroje resultados. */
  readonly tablaVisible = signal(false);

  /** Catálogos con el código también en number, para los dropdowns de consecutivo. */
  readonly oficinas = signal<(CatalogoOption & { codigoNumerico: number })[]>([]);
  readonly agentesRiesgo = signal<(CatalogoOption & { codigoNumerico: number })[]>([]);
  readonly estados = signal<CatalogoOption[]>([]);

  filtro: RiesgoPotencialFiltro = {};

  // Las fechas se editan como Date y viajan como ISO yyyy-MM-dd.
  fechaIdentificacionInicial: Date | null = null;
  fechaIdentificacionFinal: Date | null = null;
  fechaSeguimientoInicial: Date | null = null;
  fechaSeguimientoFinal: Date | null = null;

  // === Buscadores compartidos: Profesional / Consultor / Cliente ===
  mostrarBuscadorProfesional = false;
  mostrarBuscadorConsultor = false;
  mostrarBuscadorCliente = false;

  nombreProfesional = '';
  nombreConsultor = '';
  nombreCliente = '';

  // === Modal de Ver detalle (evita exponer el id del riesgo en la URL) ===
  mostrarModalSeguimiento = false;
  readonly idRiesgoSeleccionado = signal<number | null>(null);

  // === Paginación (skill paginacion: manual, sin paginador automático de PrimeNG) ===
  readonly currentPage = signal(1);
  readonly pageSize = signal(DEFAULT_ROWS_PER_PAGE);
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  selectedPageSize = DEFAULT_ROWS_PER_PAGE;

  readonly totalPages = computed(() => {
    const total = this.totalRegistros() ?? 0;
    const size = this.pageSize();
    return total > 0 ? Math.ceil(total / size) : 0;
  });

  readonly registroInicio = computed(() => {
    if ((this.totalRegistros() ?? 0) === 0) {
      return 0;
    }
    return (this.currentPage() - 1) * this.pageSize() + 1;
  });

  readonly registroFin = computed(() => {
    const total = this.totalRegistros() ?? 0;
    if (total === 0) {
      return 0;
    }
    return Math.min(this.currentPage() * this.pageSize(), total);
  });

  private nextCursor: string | null = null;
  private previousCursor: string | null = null;

  async ngOnInit(): Promise<void> {
    // Legacy: la consulta exige al menos un filtro (validarFiltrosProgramacion),
    // por lo que no se ejecuta búsqueda automática al entrar a la pantalla.
    await Promise.all([this.cargarCatalogos(), this.precargarOficinaUsuario()]);
  }

  /** Legacy: reset() -> lbOficina.setSelectedValue(oficina del usuario logueado). */
  private async precargarOficinaUsuario(): Promise<void> {
    const usuario = await this.usuarioState.cargar();
    if (usuario?.nmconsecOficina != null) {
      this.filtro.nmconsecutivoOficina = usuario.nmconsecOficina;
    }
  }

  private async cargarCatalogos(): Promise<void> {
    const [oficinas, agentes, estados] = await Promise.all([
      this.catalogoRepository.getOficinas(),
      this.catalogoRepository.getAgentesRiesgo(),
      this.catalogoRepository.getEstadosRiesgoPotencial()
    ]);

    this.oficinas.set(this.conCodigoNumerico(oficinas));
    this.agentesRiesgo.set(this.conCodigoNumerico(agentes));
    this.estados.set(estados);
  }

  private conCodigoNumerico(
    opciones: CatalogoOption[]
  ): (CatalogoOption & { codigoNumerico: number })[] {
    return opciones
      .map(opcion => ({ ...opcion, codigoNumerico: Number(opcion.codigo) }))
      .filter(opcion => Number.isFinite(opcion.codigoNumerico));
  }

  /** Legacy: Utilidades.limitString(valor, 20) en las columnas Empresa y Riesgo. */
  limitar(valor?: string): string {
    if (!valor) {
      return '';
    }
    return valor.length > 20 ? `${valor.slice(0, 20)}…` : valor;
  }

  /** Estándar GIROP de tabla (skill sura-ui 11.4.1): texto visible con fallback '-'. */
  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  /** Estándar GIROP de tabla (skill sura-ui 11.4.1): valor completo para el atributo title. */
  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }

  /**
   * Legacy: validarFechas() — el rango debe venir completo y en orden.
   * @returns mensaje de error, o null si los rangos son válidos.
   */
  private validarFechas(): string | null {
    const rangos: Array<[Date | null, Date | null]> = [
      [this.fechaIdentificacionInicial, this.fechaIdentificacionFinal],
      [this.fechaSeguimientoInicial, this.fechaSeguimientoFinal]
    ];

    for (const [inicial, final] of rangos) {
      if (!inicial && !final) {
        continue;
      }
      if (!inicial || !final || inicial.getTime() > final.getTime()) {
        return MSG_FECHAS;
      }
    }
    return null;
  }

  /** Legacy: validarFiltrosProgramacion() — al menos un criterio diligenciado. */
  private tieneAlMenosUnFiltro(): boolean {
    return !!(
      this.filtro.dsorden?.trim() ||
      this.filtro.nmconsecutivoOficina ||
      this.filtro.nmconsecutivoAsesor ||
      this.filtro.nmconsecutivoBenef ||
      this.filtro.nmconsecutivoCumpl ||
      this.filtro.nmconsecRiesgo ||
      this.filtro.nmtipoSituacion ||
      this.filtro.dniProfesional?.trim() ||
      this.filtro.cdestado ||
      this.fechaIdentificacionInicial ||
      this.fechaSeguimientoInicial
    );
  }

  private construirFiltro(): RiesgoPotencialFiltro {
    return {
      ...this.filtro,
      dsorden: this.filtro.dsorden?.trim() || undefined,
      dniProfesional: this.filtro.dniProfesional?.trim() || undefined,
      fechaIdentificacionInicial: this.toIsoDate(this.fechaIdentificacionInicial),
      fechaIdentificacionFinal: this.toIsoDate(this.fechaIdentificacionFinal),
      fechaSeguimientoInicial: this.toIsoDate(this.fechaSeguimientoInicial),
      fechaSeguimientoFinal: this.toIsoDate(this.fechaSeguimientoFinal)
    };
  }

  private toIsoDate(fecha: Date | null): string | undefined {
    if (!fecha) {
      return undefined;
    }
    const anio = fecha.getFullYear();
    const mes = String(fecha.getMonth() + 1).padStart(2, '0');
    const dia = String(fecha.getDate()).padStart(2, '0');
    return `${anio}-${mes}-${dia}`;
  }

  async buscar(): Promise<void> {
    const errorFechas = this.validarFechas();
    if (errorFechas) {
      this.messageService.add({ severity: 'warn', summary: 'Fechas', detail: errorFechas });
      return;
    }
    if (!this.tieneAlMenosUnFiltro()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Filtros requeridos',
        detail: MSG_FILTROS_REQUERIDOS
      });
      return;
    }
    this.currentPage.set(1);
    this.nextCursor = null;
    this.previousCursor = null;
    await this.cargar(null, 'NEXT');
  }

  /** Legacy: siLimpiar -> reset() (también vuelve a precargar la oficina del usuario). */
  limpiar(): void {
    this.filtro = {};
    this.fechaIdentificacionInicial = null;
    this.fechaIdentificacionFinal = null;
    this.fechaSeguimientoInicial = null;
    this.fechaSeguimientoFinal = null;
    this.nombreProfesional = '';
    this.nombreConsultor = '';
    this.nombreCliente = '';
    this.items.set([]);
    this.tablaVisible.set(false);
    this.totalRegistros.set(null);
    this.hasNext.set(false);
    this.hasPrevious.set(false);
    this.mensajeError.set(null);
    this.currentPage.set(1);
    this.nextCursor = null;
    this.previousCursor = null;
    void this.precargarOficinaUsuario();
  }

  // === Buscador de Profesional (legacy: UtilidadesBuscadores.buscarProfesional) ===
  abrirBuscadorProfesional(): void {
    this.mostrarBuscadorProfesional = true;
  }

  onProfesionalSeleccionado(profesional: Profesional): void {
    this.filtro.dniProfesional = profesional.dni;
    this.nombreProfesional = `${profesional.dni} - ${profesional.dsnombre}`;
    this.mostrarBuscadorProfesional = false;
  }

  limpiarProfesional(): void {
    this.filtro.dniProfesional = undefined;
    this.nombreProfesional = '';
  }

  // === Buscador de Consultor (legacy: identificationAsesor / IdentificationViewImpl) ===
  abrirBuscadorConsultor(): void {
    this.mostrarBuscadorConsultor = true;
  }

  onConsultorSeleccionado(consultor: AsesorSeleccionado): void {
    this.filtro.nmconsecutivoAsesor = consultor.nmconsecutivoAsesor ?? consultor.nmconsecutivo;
    const nombreCompleto = [consultor.dsnombre1, consultor.dsapellido1].filter(Boolean).join(' ');
    this.nombreConsultor = `${consultor.nmidentificacion} - ${consultor.dsnombreComercial || nombreCompleto}`;
    this.mostrarBuscadorConsultor = false;
  }

  limpiarConsultor(): void {
    this.filtro.nmconsecutivoAsesor = undefined;
    this.nombreConsultor = '';
  }

  // === Buscador de Cliente (legacy: idBeneficiariosBuscador / BuscarBeneficiarioViewImpl) ===
  abrirBuscadorCliente(): void {
    this.mostrarBuscadorCliente = true;
  }

  onClienteSeleccionado(cliente: ClienteSeleccionado): void {
    this.filtro.nmconsecutivoBenef = cliente.nmconsecutivo;
    this.nombreCliente = `${cliente.numeroIdentificacion} - ${cliente.nombreComercial}`;
    this.mostrarBuscadorCliente = false;
  }

  limpiarCliente(): void {
    this.filtro.nmconsecutivoBenef = undefined;
    this.nombreCliente = '';
  }

  /** Legacy: primera página — reinicia el cursor y consulta desde el inicio. */
  async irPrimeraPagina(): Promise<void> {
    this.currentPage.set(1);
    this.previousCursor = null;
    await this.cargar(null, 'NEXT');
  }

  /**
   * Última página — el backend interpreta PREVIOUS sin cursor como "traer la última
   * página" (ver findPreviousPage: cursor nulo = sin límite superior, ORDER BY DESC).
   */
  async irUltimaPagina(): Promise<void> {
    if (this.currentPage() >= this.totalPages()) {
      return;
    }
    this.nextCursor = null;
    await this.cargar(null, 'PREVIOUS');
    this.currentPage.set(this.totalPages());
  }

  async irPaginaAnterior(): Promise<void> {
    if (!this.hasPrevious()) {
      return;
    }
    this.currentPage.update(p => Math.max(1, p - 1));
    await this.cargar(this.previousCursor, 'PREVIOUS');
  }

  async irPaginaSiguiente(): Promise<void> {
    if (!this.hasNext()) {
      return;
    }
    this.currentPage.update(p => p + 1);
    await this.cargar(this.nextCursor, 'NEXT');
  }

  /** Legacy: cambio de tamaño de página — reinicia la paginación desde la primera página. */
  async onPageSizeChange(): Promise<void> {
    this.pageSize.set(this.selectedPageSize);
    this.currentPage.set(1);
    this.nextCursor = null;
    this.previousCursor = null;
    await this.cargar(null, 'NEXT');
  }

  private async cargar(cursor: string | null, direction: 'NEXT' | 'PREVIOUS'): Promise<void> {
    this.loading.set(true);
    this.mensajeError.set(null);
    try {
      const page = await this.repository.listarRiesgosPotenciales(
        this.construirFiltro(),
        this.pageSize(),
        cursor,
        direction
      );
      this.items.set(page.content ?? []);
      this.hasNext.set(page.hasNext);
      this.hasPrevious.set(page.hasPrevious);
      this.totalRegistros.set(page.totalElements ?? null);
      this.nextCursor = page.nextCursor;
      this.previousCursor = page.previousCursor;
      this.tablaVisible.set(true);

      if ((page.content ?? []).length === 0) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Búsqueda',
          detail: MSG_SIN_RESULTADOS
        });
      }
    } catch (error) {
      this.mensajeError.set(this.extraerMensajeError(error));
      this.tablaVisible.set(false);
    } finally {
      this.loading.set(false);
    }
  }

  private extraerMensajeError(error: unknown): string {
    const err = error as { error?: { message?: string } };
    return err?.error?.message ?? 'No fue posible consultar las alertas tempranas.';
  }

  /** Legacy: saNuevoRp — abre la aplicación externa sin datos de beneficiario. */
  nuevaAlertaTemprana(): void {
    void abrirAlertaTemprana(null, this.messageService, { permitirSinProgramacion: true });
  }

  /** Legacy: la navegación a riesgos-potenciales/:id/seguimiento exponía el id en la URL. */
  verDetalleSeguimiento(item: RiesgoPotencialListadoItem): void {
    this.idRiesgoSeleccionado.set(item.nmconsecutivo);
    this.mostrarModalSeguimiento = true;
  }

  cerrarModalSeguimiento(): void {
    this.mostrarModalSeguimiento = false;
    this.idRiesgoSeleccionado.set(null);
  }

  /** Legacy: mismo patrón de maximizarDialogoDetalle() en servicios-entregados.component.ts */
  maximizarDialogoSeguimiento(dialog: { maximized?: boolean; maximize?: (event?: Event) => void }): void {
    if (dialog.maximize) {
      try {
        dialog.maximize(new Event('click'));
      } catch {
        // No-op: fallback below keeps the state maximized when API is available.
      }
    }

    if (dialog.maximized !== true) {
      dialog.maximized = true;
    }
  }
}
